import React from "react";
import SidebarLayout from "../../PageLayout/SidebarLayout";
import GeneralSummaryCard from "../components/GeneralSummaryCard";
import TopUsers from "../components/TopUsers";
import UsageTrends from "../components/UsageTrends";
import ContextualInsights from "../components/ContextualInsights";
import QuestionHeatmap from "../components/QuestionHeatmap";
import RecentDocuments from "../components/RecentDocuments";
import useOverviewSummary from "../../hooks/useOverviewSummary";
import useTopUsers from "../../hooks/useTopUsers";
import useContextInsights from "../../hooks/useContextInsights";
import useQuestionHeatmap from "../../hooks/useQuestionHeatmap";

export default function Overview() {
  const { data, loading, error } = useOverviewSummary(30);
  const { data: topUsersData } = useTopUsers(30, 5);
  const { data: ctxData } = useContextInsights(30, 10);
  const { data: heatmapData } = useQuestionHeatmap(7, "hour_of_day");

  return (
    <SidebarLayout>
      <div className="p-4 md:p-6 space-y-4 max-w-[1400px] mx-auto w-full min-w-0">
        {/* General Summary (API) */}
        <section className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="px-4 pt-4 pb-3">
            <h2 className="text-[13px] font-semibold text-slate-800">General Summary</h2>
          </div>
          <div className="px-4 pb-4">
            {loading ? (
              <div className="text-xs text-gray-500 px-2 py-1">Loading…</div>
            ) : error ? (
              <div className="text-xs text-red-600 px-2 py-1">Failed to load overview: {String(error)}</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                {(() => {
                  const m = data?.metrics || [];
                  if (m.length === 0) {
                    return (
                      <div className="text-sm text-gray-500 col-span-full px-2 py-2">
                        No data available for the selected period.
                      </div>
                    );
                  }
                  return m.map((metric, idx) => (
                    <GeneralSummaryCard
                      key={idx}
                      title={metric.title}
                      value={metric ? metric.value : ""}
                      subtitle={metric?.subtitle}
                      bgClass={metric?.bgClass}
                      badgeClass={metric?.badgeClass}
                      icon={metric?.icon}
                    />
                  ));
                })()}
              </div>
            )}
          </div>
        </section>

        {/* Middle + Bottom */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start min-w-0">
          {/* Left (2 cols): stack to avoid blank space under shorter cards */}
          <div className="md:col-span-2 min-w-0 flex flex-col gap-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 min-w-0">
              <TopUsers users={topUsersData.users || []} />
              <UsageTrends />
            </div>
            <div className="min-w-0">
              <RecentDocuments />
            </div>
          </div>

          {/* Right (1 col): stack */}
          <div className="min-w-0 flex flex-col gap-4">
            <ContextualInsights insights={ctxData.insights || []} />
            <div className="min-w-0">
              <QuestionHeatmap data={heatmapData || null} />
            </div>
          </div>
        </div>

      </div>
    </SidebarLayout>
  );
}
